/**
 * Contract for feedback-service's transactional feedback, mirrored from the miniapp that already
 * consumes it (`transactional-feedback-miniapp`) so the two agree on the wire shape.
 */

export enum CandidateStatusEnum {
  UNSEEN = 'UNSEEN',
  SEEN = 'SEEN',
  DISMISSED = 'DISMISSED',
  SUBMITTED = 'SUBMITTED',
}

/** `POST /api/feedback-candidates/:uuid/submit`. Rating is 1–5; comment is capped at 500 server-side. */
export interface SubmitFeedbackDto {
  rating: number;
  comment?: string;
}

/**
 * `POST /:uuid/submit` answers **201** with the id of the rating it created — nothing about the
 * candidate. Confirmation is therefore the 201 itself; there is no `success` flag or status to read.
 * (`{ success, candidate: { status } }` is the PATCH `/:uuid/status` response — a different endpoint.)
 */
export interface SubmitFeedbackResponse {
  feedback_uuid: string;
}

/**
 * One candidate with no rating yet, from `GET /api/feedback-candidates/unrated`.
 *
 * The list read behind the feed, as opposed to `GET /feedback-candidates`, which answers "should I
 * interrupt this user right now" and returns a single candidate. That single row is why only one card
 * per page could ever show live stars.
 *
 * Carries no display copy: resolving it costs an upstream call per candidate under the reader's own
 * bearer, and the feed already holds its own titles.
 */
export interface UnratedFeedbackCandidate {
  uuid: string;
  feature_vertical: string;
  /**
   * The primary join key: a work-order uuid on SERVICE_REQUEST, a booking uuid on RESOURCE_BOOKING.
   *
   * ⚠️ On SERVICE_BOOKING it is a **booking** uuid from one producer and an **event** uuid from the
   * other, so `entity_uuid` has to be matched too or half the events silently miss.
   */
  transaction_uuid: string;
  /**
   * What the feedback is about — the resource, the event — and null on SERVICE_REQUEST and on every
   * candidate minted before the column shipped. A second join key, never a required one.
   */
  entity_uuid: string | null;
  transaction_completed_at: string;
  /** `UNSEEN` | `SEEN` | `DISMISSED`. Every candidate in this list is submittable; `SUBMITTED` drops out. */
  status: string;
  /** Off the settings row the endpoint already joins, which is what retires the dependent detail read. */
  comments_enabled: boolean;
}

export interface GetUnratedCandidatesResponse {
  /** Empty array rather than null when there are none: this is a list, not a prompt decision. */
  candidates: UnratedFeedbackCandidate[];
}

/** Longest comment the service accepts — enforced here so the textarea can't produce a 400. */
export const COMMENT_MAX_LENGTH = 500;

/**
 * The feedback state for one activity item, as the tenant feed will carry it once the read-time join
 * lands (VIS-1538): the candidate to submit against, whether this building collects comments, and the
 * rating if one was already given.
 *
 * Absent today — nothing projects it yet — which is why submitting is optimistic (see
 * `use-feedback` context).
 */
export interface ActivityFeedback {
  candidateUuid: string;
  commentsEnabled: boolean;
  /** Set once submitted. Presence means the item is read-only: feedback is given once, never edited. */
  rating?: number;
}
